import { useEffect, useState } from "react";
import { Plus, Trash2, Heart, Flame } from "lucide-react";
import { api } from "@/lib/apiClient";
import PageHeader from "@/components/PageHeader";

export default function Faith() {
    const [verse, setVerse] = useState(null);
    const [reflections, setReflections] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        date: new Date().toISOString().slice(0, 10),
        type: "daily", reflection: "", prayer: "", scripture_ref: "",
        patience_rating: 5, emotional_rating: 5, plan_adherence: 5,
    });

    const load = async () => {
        const [v, r] = await Promise.all([api.get("/scripture/today"), api.get("/reflections")]);
        setVerse(v.data);
        setReflections(r.data.sort((a, b) => (b.date || "").localeCompare(a.date || "")));
    };
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        await api.post("/reflections", form);
        setForm({ ...form, reflection: "", prayer: "", scripture_ref: "" });
        setOpen(false);
        load();
    };
    const del = async (id) => { await api.delete(`/reflections/${id}`); load(); };

    return (
        <div className="space-y-8" data-testid="faith-page">
            <PageHeader
                eyebrow="Faith informs the hand"
                title="Faith & Discipline"
                subtitle="Daily Scripture, honest reflection, and prayer. Scripture is for guidance — never a market prediction."
                action={
                    <button onClick={() => setOpen(!open)} className="bg-[#D4AF37] text-[#0E0E10] px-3 py-2 rounded-md hover:bg-[#B3922E] text-sm inline-flex items-center gap-2" data-testid="reflect-new-btn">
                        <Plus className="w-4 h-4" /> New reflection
                    </button>
                }
            />

            {verse && (
                <section className="twp-card p-6 md:p-8" data-testid="scripture-feature">
                    <div className="caption">Today · {verse.translation}</div>
                    <blockquote className="font-serif text-2xl md:text-3xl mt-4 leading-snug">
                        &ldquo;{verse.text}&rdquo;
                    </blockquote>
                    <div className="mt-3 gold font-mono text-sm">— {verse.ref}</div>
                    <div className="mt-6 grid md:grid-cols-2 gap-6">
                        <div>
                            <div className="caption">Reflect</div>
                            <p className="mt-2 text-sm text-zinc-300 leading-relaxed">{verse.reflection_prompt}</p>
                        </div>
                        <div>
                            <div className="caption">Pray</div>
                            <p className="mt-2 text-sm text-zinc-300 italic leading-relaxed">{verse.prayer_prompt}</p>
                        </div>
                    </div>
                    <div className="mt-6 text-[11px] text-zinc-600">{verse.disclaimer}</div>
                </section>
            )}

            {open && (
                <form onSubmit={save} className="twp-card p-6 grid md:grid-cols-2 gap-3" data-testid="reflect-form">
                    <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" />
                    <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="reflect-type">
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                    </select>
                    <input placeholder="Scripture reference (e.g. Proverbs 3:5-6)" value={form.scripture_ref} onChange={(e) => setForm({ ...form, scripture_ref: e.target.value })} className="md:col-span-2 bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" />
                    <textarea placeholder="Reflection" value={form.reflection} onChange={(e) => setForm({ ...form, reflection: e.target.value })} rows={3} className="md:col-span-2 bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="reflect-text" />
                    <textarea placeholder="Prayer" value={form.prayer} onChange={(e) => setForm({ ...form, prayer: e.target.value })} rows={3} className="md:col-span-2 bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" />
                    {[
                        { k: "patience_rating", label: "Patience" },
                        { k: "emotional_rating", label: "Emotional readiness" },
                        { k: "plan_adherence", label: "Plan adherence" },
                    ].map((f) => (
                        <div key={f.k} className="flex items-center gap-3 md:col-span-2">
                            <label className="caption w-40">{f.label}</label>
                            <input type="range" min={1} max={10} value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: parseInt(e.target.value) })} className="flex-1" />
                            <span className="font-mono gold w-8 text-right">{form[f.k]}/10</span>
                        </div>
                    ))}
                    <button type="submit" className="bg-[#D4AF37] text-[#0E0E10] px-4 py-2 rounded-md hover:bg-[#B3922E] text-sm md:col-span-2" data-testid="reflect-save">Save reflection</button>
                </form>
            )}

            <section className="space-y-3" data-testid="reflections-list">
                <div className="caption">Reflections</div>
                {reflections.length === 0 && <div className="text-sm text-zinc-500">No reflections yet.</div>}
                {reflections.map((r) => (
                    <div key={r.id} className="twp-card p-5">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <span className="caption">{r.date} · {r.type}</span>
                                {r.scripture_ref && <span className="font-mono gold text-xs">{r.scripture_ref}</span>}
                            </div>
                            <button onClick={() => del(r.id)} className="text-zinc-500 hover:text-[#B95050]"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        {r.reflection && <p className="mt-3 text-sm text-zinc-300 leading-relaxed">{r.reflection}</p>}
                        {r.prayer && <p className="mt-3 text-sm text-zinc-400 italic border-l-2 border-[#D4AF37]/30 pl-3">{r.prayer}</p>}
                        <div className="mt-3 flex items-center gap-4 text-xs text-zinc-500">
                            <span className="flex items-center gap-1"><Heart className="w-3.5 h-3.5" /> Patience <span className="gold font-mono">{r.patience_rating ?? "—"}</span></span>
                            <span className="flex items-center gap-1"><Flame className="w-3.5 h-3.5" /> Readiness <span className="gold font-mono">{r.emotional_rating ?? "—"}</span></span>
                            <span>Plan <span className="gold font-mono">{r.plan_adherence ?? "—"}</span></span>
                        </div>
                    </div>
                ))}
            </section>
        </div>
    );
}
